// owner/ui/UnitBits.js — small pieces every owner screen shows about a unit: its house-shaped photo, its
// «حالة التشغيل» chip and its type line. Words come from the locales; nothing here formats a date.

import React from 'react';
import useLanguage from '../../hooks/useLanguage';
import { countWith } from '../../utils/localeText';
import { formatCountLatn } from '../../utils/numberFormatter';

const TINTS = ['', ' t2', ' t3'];

/** The unit's photo in the house shape; a tinted house when it has none. `index` varies the tint in a list. */
export const UnitThumb = ({ photo, index = 0, large = false }) => {
  const cls = `o-house${large ? ' lg' : ''}${photo ? '' : TINTS[index % TINTS.length]}`;
  return photo
    ? <img className={cls} src={photo} alt="" loading="lazy" />
    : <span className={cls} aria-hidden="true" />;
};

// Owner view codes (be/routes/unitStatusRoutes.js decorateForViewer) → chip colour.
const STATUS_CHIP = { OCCUPIED: 'occupied', AVAILABLE: 'staying', PREPARING: 'upcoming', STOPPED: '' };

/** «حالة التشغيل» chip; nothing while the status is unknown, never a guessed one. */
export const UnitStatusChip = ({ status }) => {
  const { t } = useLanguage();
  if (!status || !(status in STATUS_CHIP)) return null;
  return <span className={`o-chip ${STATUS_CHIP[status]}`.trim()}>{t(`ownerApp.unitStatus.${status}`)}</span>;
};

/** "شقة · غرفتا نوم" — the stored Arabic type, translated, and the bedroom count (0 = studio). */
export const useUnitTypeLine = () => {
  const { t } = useLanguage();
  return (unit) => {
    const types = t('ownerApp.unitType');
    const parts = [];
    if (unit?.type) parts.push((types && types[unit.type]) || unit.type);
    const bedrooms = unit?.specs?.bedrooms;
    if (bedrooms === 0) parts.push(t('ownerApp.common.studio'));
    else if (Number.isInteger(bedrooms)) parts.push(countWith(bedrooms, t('ownerApp.common.bedrooms'), formatCountLatn));
    return parts;
  };
};
